import React from 'react';
import styled, { css } from 'styled-components';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  style?: React.CSSProperties;
  as?: React.ElementType;
}

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant', 'size', 'fullWidth', 'loading', 'as'].includes(prop),
})<ButtonProps>` display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  outline: none;
  
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          height: 32px;
          padding: 0 14px;
          font-size: 13px;
        `;
      case 'large':
        return css`
          height: 48px;
          padding: 0 22px;
          font-size: 15px;
        `;
      default:
        return css`
          height: 40px;
          padding: 0 18px;
          font-size: 14px;
        `;
    }
  }}

  ${({ variant }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: #fff;
          color: #475569;
          border: 1px solid #e2e8f0;

          &:hover:not(:disabled) {
            background-color: #f8fafc;
            border-color: #cbd5e1;
          }
        `;
      case 'danger':
        return css`
          background-color: #fff;
          color: #dc2626;
          border: 1px solid #fecaca;

          &:hover:not(:disabled) {
            background-color: #fef2f2;
            border-color: #fca5a5;
          }
        `;
      case 'success':
        return css`
          background-color: #16a34a;
          color: white;
          border: 1px solid #16a34a;

          &:hover:not(:disabled) {
            background-color: #15803d;
            border-color: #15803d;
          }
        `;
      default:
        return css`
          background-color: #111827;
          color: white;
          border: 1px solid #111827;

          &:hover:not(:disabled) {
            background-color: #1f2937;
            border-color: #1f2937;
          }
        `;
    }
  }}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  ${({ loading }) =>
    loading &&
    css`
      position: relative;
      color: transparent;
      
      &::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(148, 163, 184, 0.35);
        border-top-color: #64748b;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
`;

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  loading = false,
  children,
  onClick,
  type = 'button',
  as,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      loading={loading}
      onClick={onClick}
      type={type}
      as={as}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button;